import { useRef, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { Panel } from '@/components/ui';
import { type Deal, PIPELINE_STATUS_COLORS } from '@/types';
import { formatRelativeTime } from '@/lib/format';
import { StatusDropdown } from './StatusDropdown';

// Aligned with cockpit canonical 7-col contract (ADR 0018, cmv Phase 5 S5):
// Deal Name · Units · Basis · IRR · DSCR · Market · Status. Pipeline page
// keeps the trailing Updated + Open columns for table-level metadata + nav.
// YoC dropped 2026-05-25 — was diverging from the cockpit Your Pipeline panel.
const COLUMNS = [
  'Deal Name',
  'Units',
  'Basis',
  'IRR',
  'DSCR',
  'Market',
  'Status',
  'Updated',
  '',
];

interface DealTableProps {
  deals: Deal[];
  activeDeal: number;
  selectedDeal: number;
  activeStatus: string | null;
  onSelectDeal: (index: number) => void;
  onHoverDeal: (index: number) => void;
  onLeaveDeal: () => void;
}

export function DealTable({
  deals,
  activeDeal,
  selectedDeal,
  activeStatus,
  onSelectDeal,
  onHoverDeal,
  onLeaveDeal,
}: DealTableProps) {
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);

  const handleSelect = useCallback(
    (i: number) => {
      onSelectDeal(i);
      setTimeout(() => {
        rowRefs.current[i]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 50);
    },
    [onSelectDeal]
  );

  return (
    <Panel title="Deal Pipeline" className="flex-1 overflow-hidden">
      {/* C1: always render thead + tbody — zone stays intact even when deals
          is empty. "No deals" is a subtle inline caption inside the zone,
          NOT an EmptyState card that replaces it. ADR 0021: no + New Deal. */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {COLUMNS.map((h) => (
                <th
                  key={h}
                  className="text-left px-2 py-1.5 text-[8px] text-text-dim font-mono font-medium border-b border-panel-border uppercase tracking-[0.6px]"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {deals.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="px-3 py-6 text-center"
                  data-testid="deal-table-empty"
                >
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-[10px] font-mono text-text-dim tracking-[0.6px]">
                      No deals yet — start one in the Builder.
                    </span>
                    <Link
                      to="/builder"
                      search={{ tab: 'workbench' as const }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-dim border border-accent/15 rounded-[2px] text-accent text-[10px] font-mono font-semibold tracking-[0.4px] uppercase cursor-pointer hover:bg-accent/10 transition-colors"
                      data-testid="pipeline-empty-add-deal"
                    >
                      + Add Deal
                    </Link>
                  </div>
                </td>
              </tr>
            ) : (
              deals.map((d, i) => {
                const sc =
                  PIPELINE_STATUS_COLORS[d.status] || 'var(--color-text-dim)';
                const matchesFilter =
                  !activeStatus || d.status === activeStatus;
                const isActive = activeDeal === i;

                return (
                  <tr
                    key={d.id}
                    ref={(el) => {
                      rowRefs.current[i] = el;
                    }}
                    className="deal-row cursor-pointer transition-opacity duration-300"
                    style={{
                      opacity: matchesFilter ? 1 : 0.25,
                      background: isActive
                        ? `color-mix(in srgb, ${sc} ${
                            selectedDeal === i ? '9' : '5'
                          }%, transparent)`
                        : undefined,
                      boxShadow: isActive
                        ? selectedDeal === i
                          ? `inset 3px 0 0 ${sc}`
                          : `inset 2px 0 0 color-mix(in srgb, ${sc} 53%, transparent)`
                        : undefined,
                    }}
                    onClick={() => handleSelect(i)}
                    onMouseEnter={() => {
                      if (selectedDeal < 0) onHoverDeal(i);
                    }}
                    onMouseLeave={() => {
                      if (selectedDeal < 0) onLeaveDeal();
                    }}
                    data-testid="deal-row"
                  >
                    {/* Deal Name — bright, left-border accent */}
                    <td
                      className="px-2 py-1.5 text-[11px] font-mono text-text-bright font-medium"
                      style={{
                        borderLeft: `2px solid ${sc}`,
                        paddingLeft: '14px',
                      }}
                    >
                      {d.name}
                    </td>
                    {/* Units */}
                    <td className="px-2 py-1.5 text-[11px] font-mono text-text-muted tabular-nums">
                      {Number.isFinite(d.units) ? d.units : '—'}
                    </td>
                    {/* Basis */}
                    <td className="px-2 py-1.5 text-[11px] font-mono text-text-muted tabular-nums">
                      {d.basis ?? '—'}
                    </td>
                    {/* IRR */}
                    <td className="px-2 py-1.5 text-[11px] font-mono text-text-muted tabular-nums">
                      {d.irr ?? '—'}
                    </td>
                    {/* DSCR (canonical 7-col contract; was YoC pre-2026-05-25) */}
                    <td className="px-2 py-1.5 text-[11px] font-mono text-text-muted tabular-nums">
                      {d.dscr ?? '—'}
                    </td>
                    {/* Market */}
                    <td className="px-2 py-1.5 text-[11px] font-mono text-text-muted tabular-nums">
                      {d.market ?? '—'}
                    </td>
                    {/* Status — Lovable DEAL STATUS dropdown (owner-write, arbitrary set) */}
                    <td className="px-2 py-1.5">
                      <StatusDropdown conversationId={d.id} status={d.status} />
                    </td>
                    {/* Updated */}
                    <td className="px-2 py-1.5 text-[11px] font-mono text-text-dim tabular-nums">
                      {formatRelativeTime(d.updatedAt)}
                    </td>
                    {/* Open → — carries `conversation=<id>` so the Builder
                        loads THIS deal instead of falling back to the last
                        sidebar-selected conversation (the TanStack Router
                        `builderSearchSchema` parses `conversation:
                        z.string().uuid().optional()` since model-view-ux-polish
                        Phase 2; <BuilderPage> seeds + syncs activeConversationId
                        from this param via use-search + a mount-aware effect). */}
                    <td className="px-2 py-1.5">
                      <Link
                        to="/builder"
                        search={{
                          tab: 'workbench' as const,
                          conversation: d.id,
                        }}
                        className="text-accent text-[9px] font-mono cursor-pointer"
                        data-testid="deal-row-open-link"
                      >
                        Open &rarr;
                      </Link>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
